import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useHistory } from "react-router-dom";
import {
  ChevronDownIcon,
  FunnelIcon,
  ArrowRightIcon,
  QrCodeIcon,
} from "@heroicons/react/24/solid";

import DashboardFilterUI, { DashboardFiltersPayload } from "../DashboardFilter";

import CDRCMappingBarChart from "./Dashboard/CDRCMappingBarChart";
import KeeperMappingBarChart from "./Dashboard/KeeperMappingBarChart";
import AL3MappingBarChart from "./Dashboard/AL3MappingBarChart";
import ProdCodePieChart from "./Dashboard/ProdCodePieChart";
import CarrierStatusPieChart from "./Dashboard/CarrierStatusPieChart";
import MappingPercentageCard from "./Dashboard/MappingPercentageCard";
import RecentDataMappingActivity from "./Dashboard/RecentDataMappingActivity";

import { useSearchProducerCodeDashboard } from "../hooks/useDashboardSearch";
import { toast } from "components/toastNotification";
import DownloadButton from "../../../../components/DownloadButton";
import { downloadMultiSheetExcel } from "../../../../actions/CarrierAgreementMgmt/ProducerCodeConfig";
import { useRecoilValue } from "recoil";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../permissions";

const BLUES = ["#1E3A8A", "#60A5FA", "#0C77BD", "#0A1F5A"];
const PCODE_COLORS = ["#0078D7", "#EC4899"];
const PROD_COLORS = ["#2563EB", "#F59E0B"];

type CountsDto = {
  MappedCDRC?: number;
  NotMappedCDRC?: number;
  ExceptionCDRC?: number;
  MappedKeeper?: number;
  NotMappedKeeper?: number;
  ExceptionKeeper?: number;
  MappedAL3?: number;
  NotMappedAL3?: number;
  ExceptionAL3?: number;
  TotalRows?: number;

  ActiveCount?: number;
  InactiveCount?: number;

  InProductionCarrierCount?: number;
  NotInProductionCarrierCount?: number;

  MappedCDRCPct?: number;
  MappedKeeperPct?: number;
  MappedAL3Pct?: number;

  [k: string]: any;
};

const Card: React.FC<{
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  subtitleRight?: boolean;
  className?: string;
  headerVariant?: "blue" | "plain" | "none";
}> = ({
  title,
  subtitle,
  subtitleRight = false,
  className = "",
  headerVariant = "blue",
  children,
}) => {
  const headerWrapCls =
    headerVariant === "blue"
      ? "bg-blue-50 rounded-t-xl px-4 py-2 border-b border-blue-100"
      : headerVariant === "plain"
        ? "bg-white rounded-t-xl px-4 py-2"
        : "";
  const titleCls = "text-primary text-sm font-semibold !text-left";
  const subtitleRightCls =
    headerVariant === "blue"
      ? "ml-auto text-xs text-blue-700/50"
      : "ml-auto text-xs text-gray-500";

  return (
    <div
      className={`bg-white rounded-xl border border-gray-100 shadow-sm ${className}`}
    >
      {title && headerVariant !== "none" && (
        <div className={headerWrapCls}>
          <div className="flex items-center gap-3">
            <div className={titleCls}>{title}</div>

            {subtitleRight && subtitle ? (
              <div className={subtitleRightCls}>{subtitle}</div>
            ) : null}
          </div>
        </div>
      )}

      {headerVariant !== "none" && !subtitleRight && subtitle ? (
        <div className="px-4 pt-2 text-xs text-primary-500 !text-left">
          {subtitle}
        </div>
      ) : null}
      <div className="p-4">{children}</div>
    </div>
  );
};

const DotsLegend: React.FC<{ items: { label: string; color: string }[] }> = ({
  items,
}) => (
  <div className="flex flex-wrap justify-center items-center w-full gap-x-4 gap-y-2 mt-2">
    {items.map((it) => (
      <div
        key={it.label}
        className="flex items-center gap-2 text-xs text-gray-600"
      >
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ background: it.color }}
        />
        <span>{it.label}</span>
      </div>
    ))}
  </div>
);

export const getNumericCount = (
  counts: CountsDto | null | undefined,
  key: string,
): number => {
  if (!counts) return 0;

  const tryGet = (k: string) => {
    if (Object.prototype.hasOwnProperty.call(counts, k))
      return (counts as any)[k];
    return undefined;
  };

  const camel = key.charAt(0).toLowerCase() + key.slice(1);
  const lower = key.toLowerCase();

  let raw = tryGet(key);
  if (raw === undefined) raw = tryGet(camel);
  if (raw === undefined) raw = tryGet(lower);

  if (raw === undefined) {
    const foundKey = Object.keys(counts).find(
      (k) => k.toLowerCase() === key.toLowerCase(),
    );
    if (foundKey) raw = (counts as any)[foundKey];
  }

  const n = Number(raw ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export const getMappedPct = (
  counts: CountsDto | null | undefined,
  pctKey: string,
  mappedKey: string,
): number => {
  if (counts == null) return 0;

  const pctRaw = (counts as any)[pctKey];
  if (typeof pctRaw === "number" && !Number.isNaN(pctRaw)) return pctRaw;

  const total = getNumericCount(counts, "TotalRows");
  if (!total) return 0;
  const mapped = getNumericCount(counts, mappedKey);
  return (mapped / total) * 100;
};

export const renderTotal = (
  counts: CountsDto | null | undefined,
  axisTotalKey?: string,
  fallback?: number | null,
): string | number => {
  if (axisTotalKey) {
    const v = getNumericCount(counts, axisTotalKey);
    if (v != null) return Number(v).toLocaleString();
  }
  const total = getNumericCount(counts, "TotalRows") || fallback;
  if (total == null) return "—";
  return Number(total).toLocaleString();
};

function applyFranchise(dto: any, franchise?: { value?: any } | null) {
  const raw = franchise?.value;
  if (raw == null || String(raw) === "") return;

  const n = Number(raw);
  if (Number.isNaN(n)) return;

  if (n === 3) {
    dto.IsFranchise = false;
  } else {
    dto.IsFranchise = true;
    dto.FranchiseTypeId = n;
  }
}

function applyNumeric(dto: any, key: string, value: any) {
  if (value == null || String(value) === "") return;

  const n = Number(value);
  if (!Number.isNaN(n)) dto[key] = n;
}

function applyString(dto: any, key: string, value: any) {
  if (value == null) return;

  const str = String(value).trim();
  if (str !== "") dto[key] = str;
}

function applyStringArray(dto: any, key: string, arr?: any[]) {
  if (!Array.isArray(arr) || !arr.length) return;

  const cleaned = arr
    .map((v) =>
      typeof v === "object" && v !== null && "value" in v
        ? String(v.value)
        : String(v),
    )
    .map((v) => v.trim())
    .filter((v) => v !== "");

  if (cleaned.length) dto[key] = cleaned;
}

function applyOffice(dto: any, offices?: any[]) {
  if (!Array.isArray(offices) || !offices.length) return;

  const vals = offices.map((o) => String(o.value));
  dto.OfficeCodes = vals;

  if (vals.length === 1) {
    dto.OfficeCode = vals[0];
  }
}

const ProducerCodeDashboard: React.FC<{
  onOpenModule?: () => void;
  onOpenNotifications?: () => void;
}> = ({ onOpenModule, onOpenNotifications }) => {
  const history = useHistory();
  const { loading, error, counts, search } = useSearchProducerCodeDashboard();

  useEffect(() => {
    search();
  }, [search]);

  const [filterOpen, setFilterOpen] = useState(true);
  const [version, setVersion] = useState(0);
  const prevLoadingRef = useRef<boolean>(false);
  const manualClearRef = useRef<boolean>(false);

  const isErrorFromSubSearch = !!error;

  const userPermissions = useRecoilValue(permissionsSelector);

  const canViewRecentActivity = userPermissions.includes(
    PERMISSIONS.fieldpermission_carrier_notifications,
  );

  const buildSearchDto = useCallback((p: DashboardFiltersPayload) => {
    const dto: any = {};

    applyFranchise(dto, p.franchiseType);
    applyNumeric(dto, "CarrierId", p.carrier?.value);
    applyOffice(dto, p.offices);
    applyStringArray(dto, "StateCodes", p.states);
    applyString(dto, "AcquisitionName", p.acquisition?.value);
    applyStringArray(dto, "BrandName", p.brand);
    applyNumeric(dto, "InProduction", p.inProduction?.value);
    applyNumeric(dto, "ChannelId", p.channel?.value);

    return dto;
  }, []);

  const [lastFiltersPayload, setLastFiltersPayload] =
    useState<DashboardFiltersPayload | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleFilterApply = useCallback(
    async (payload: DashboardFiltersPayload) => {
      const dto = buildSearchDto(payload);

      setLastFiltersPayload(payload);

      toast.fire("Searching", "Searching for mapping records…", "info");

      try {
        const res = await search(dto);

        const totalRows =
          (res?.counts &&
            (res.counts.TotalRows ?? (res.counts as any).totalRows)) ??
          null;

        const isFiltered = dto && Object.keys(dto).length > 0;

        if (isFiltered && (totalRows === 0 || totalRows === null)) {
          toast.fire(
            "No Results",
            "No records matched the current filters.",
            "warning",
          );
          return;
        }

        toast.fire(
          "Search Complete",
          `${(totalRows ?? 0).toLocaleString()} record(s) found`,
          "success",
        );
      } catch {
        toast.fire("Search Failed", "Failed to load results.", "error");
      }
    },
    [buildSearchDto, search],
  );

  const handleFilterClear = useCallback(async () => {
    manualClearRef.current = true;

    toast.fire("Searching", "Reloading overall totals…", "info");

    setVersion((v) => v + 1);
    try {
      const res = await search();
      setLastFiltersPayload(null);

      const totalRows =
        (res?.counts &&
          (res.counts.TotalRows ?? (res.counts as any).totalRows)) ??
        0;

      toast.fire(
        "Filters Cleared",
        `${totalRows.toLocaleString()} record(s) found`,
        "success",
      );
    } catch {
      toast.fire("Search Failed", "Failed to load results.", "error");
    }
  }, [search]);

  const handleDownload = useCallback(async () => {
    try {
      setDownloadLoading(true);
      const payload = lastFiltersPayload ?? ({} as DashboardFiltersPayload);
      const dto = buildSearchDto(payload);

      const response = await downloadMultiSheetExcel(dto);

      if (!response || !response.data) {
        toast.fire(
          "No Data",
          "No data available for the selected filters.",
          "warning",
        );
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      if ((blob as any).size === 0) {
        toast.fire(
          "No Data",
          "No data available for the selected filters.",
          "warning",
        );
        return;
      }

      const contentDisposition = response.headers?.["content-disposition"];
      let filename = `ProducerCodesReport_${new Date().toISOString()}.xlsx`;

      if (contentDisposition) {
        const utf8Match = contentDisposition.match(
          /filename\*\s*=\s*UTF-8''([^;]+)/,
        );

        const normalMatch = contentDisposition.match(
          /filename\s*=\s*"?([^";]+)"?/,
        );

        filename = decodeURIComponent(
          utf8Match?.[1] || normalMatch?.[1] || filename,
        );
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.fire("Download Ready", "Excel file downloaded.", "success");
    } catch (err: any) {
      if (err?.message?.toLowerCase() === "no data result") {
        toast.fire(
          "No Data to Export",
          "There are no records matching the selected filters",
          "warning",
        );

        return;
      }
      toast.fire(
        "Download Failed",
        "Failed to download Excel. Try again.",
        "error",
      );
    } finally {
      setDownloadLoading(false);
    }
  }, [lastFiltersPayload, buildSearchDto]);

  const prodCodePieData = [
    {
      name: "Active",
      value: getNumericCount(counts, "ActiveCount"),
      color: PCODE_COLORS[0],
    },
    {
      name: "Inactive",
      value: getNumericCount(counts, "InactiveCount"),
      color: PCODE_COLORS[1],
    },
  ];
  const inProductionPieData = [
    {
      name: "In Production",
      value: getNumericCount(counts, "InProductionCarrierCount"),
      color: PROD_COLORS[0],
    },
    {
      name: "Not In Production",
      value: getNumericCount(counts, "NotInProductionCarrierCount"),
      color: PROD_COLORS[1],
    },
  ];

  const inProductionPieTotal = inProductionPieData.reduce(
    (sum, d) => sum + (Number(d.value) || 0),
    0,
  );

  const mappedPctRows = [
    {
      name: "CDRC Mapped",
      pct: getMappedPct(counts, "MappedCDRCPct", "MappedCDRC"),
    },
    {
      name: "Keeper Mapped",
      pct: getMappedPct(counts, "MappedKeeperPct", "MappedKeeper"),
    },
    {
      name: "AL3 Mapped",
      pct: getMappedPct(counts, "MappedAL3Pct", "MappedAL3"),
    },
  ];

  const cdrcBarData = useMemo(() => {
    const mapped = getNumericCount(counts, "MappedCDRC");
    const notMapped = getNumericCount(counts, "NotMappedCDRC");
    const exception = getNumericCount(counts, "ExceptionCDRC");
    const inactive = getNumericCount(counts, "InactiveCDRC");

    return [
      { name: "Mapped", count: mapped, color: BLUES[0] },
      { name: "Not Mapped", count: notMapped, color: BLUES[1] },
      { name: "Exception", count: exception, color: BLUES[2] },
      { name: "Inactive", count: inactive, color: BLUES[3] },
    ];
  }, [counts]);

  const keeperBarData = useMemo(() => {
    const mapped = getNumericCount(counts, "MappedKeeper");
    const notMapped = getNumericCount(counts, "NotMappedKeeper");
    const exception = getNumericCount(counts, "ExceptionKeeper");
    const inactive = getNumericCount(counts, "InactiveKeeper");

    return [
      { name: "Mapped", count: mapped, color: BLUES[0] },
      { name: "Not Mapped", count: notMapped, color: BLUES[1] },
      { name: "Exception", count: exception, color: BLUES[2] },
      { name: "Inactive", count: inactive, color: BLUES[3] },
    ];
  }, [counts]);

  const al3BarData = useMemo(() => {
    const mapped = getNumericCount(counts, "MappedAL3");
    const notMapped = getNumericCount(counts, "NotMappedAL3");
    const exception = getNumericCount(counts, "ExceptionAL3");
    const inactive = getNumericCount(counts, "InactiveAL3");

    return [
      { name: "Mapped", count: mapped, color: BLUES[0] },
      { name: "Not Mapped", count: notMapped, color: BLUES[1] },
      { name: "Exception", count: exception, color: BLUES[2] },
      { name: "Inactive", count: inactive, color: BLUES[3] },
    ];
  }, [counts]);

  return (
    <div className="px-8 py-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center pb-2">
          <QrCodeIcon className="text-white bg-blue-500 rounded-md h-7 w-7  p-1" />
          <div className="text-gray-600 font-medium ml-2">
            <h3 className="text-xs font-medium text-gray-500">
              CDRC-Keeper-AL3 Mapping to IQ Overview
            </h3>
            <h1 className="mr-auto font-bold text-primary text-md uppercase">
              CARRIER PRODUCER CODES
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`ml-2 ${
              downloadLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <DownloadButton
              buttonValue={downloadLoading ? "Downloading..." : "Download File"}
              onClick={handleDownload}
              disabled={downloadLoading}
            />
          </button>

          <button
            type="button"
            onClick={
              onOpenModule ??
              (() =>
                history.push("/carrieragreementmgmt/producercodeconfig", {
                  view: "table",
                }))
            }
            className="text-quaternary border border-current opacity-100 hover:scale-90 transition duration-500 shadow-md bg-transparent group relative flex items-center justify-center py-1 px-3 text-xs font-medium rounded-full hover:text-white hover:bg-quaternary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-quaternary"
          >
            <span className="mr-2 inline-flex items-center justify-center rounded-full h-4 w-4 bg-quaternary group-hover:bg-white transition">
              <ArrowRightIcon
                className="h-3 w-3 text-white group-hover:text-quaternary"
                aria-hidden="true"
              />
            </span>
            Go to Producer Codes Table
          </button>
        </div>
      </div>

      <Card className="overflow-visible z-30">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-primary">
              <span>Filter By</span>
              <FunnelIcon className="h-4 w-4" />
            </div>
          </div>

          <div>
            <button
              type="button"
              aria-expanded={filterOpen}
              onClick={() => setFilterOpen((prev) => !prev)}
              className="inline-flex items-center justify-center p-1 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-quaternary"
            >
              <ChevronDownIcon
                className={`h-5 w-5 transform transition-transform duration-200 ${
                  filterOpen ? "rotate-0" : "-rotate-90"
                }`}
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <div className="border-b border-gray-200 mb-3" />

        <div
          className={`transition-[max-height,opacity] duration-200 overflow-hidden ${
            filterOpen ? "max-h-[1200px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="px-0 pb-6">
            <DashboardFilterUI
              onApply={handleFilterApply}
              onClear={handleFilterClear}
            />
          </div>
        </div>

        <div className="px-0">
          <div className="space-y-4">
            {/* TOP ROW */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card
                title="CDRC Mapping to IQ"
                subtitleRight
                subtitle={
                  <span className="italic text-primary">
                    Total of{" "}
                    <span className="font-bold">
                      {renderTotal(counts, "TotalRows")}
                    </span>
                  </span>
                }
              >
                <div className="h-44 mb-2 mt-2">
                  <CDRCMappingBarChart data={cdrcBarData} loading={loading} />
                </div>
                <DotsLegend
                  items={[
                    { label: "Mapped-CDRC", color: BLUES[0] },
                    { label: "Not Mapped-CDRC", color: BLUES[1] },
                    { label: "Exception-CDRC", color: BLUES[2] },
                    { label: "Inactive-CDRC", color: BLUES[3] },
                  ]}
                />
              </Card>

              <Card
                title="Keeper Mapping to IQ"
                subtitleRight
                subtitle={
                  <span className="italic text-primary">
                    Total of{" "}
                    <span className="font-bold">
                      {renderTotal(counts, "TotalRows")}
                    </span>
                  </span>
                }
              >
                <div className="h-44 mb-2 mt-2">
                  <KeeperMappingBarChart
                    data={keeperBarData}
                    loading={loading}
                  />
                </div>
                <DotsLegend
                  items={[
                    { label: "Mapped-Keeper", color: BLUES[0] },
                    { label: "Not Mapped-Keeper", color: BLUES[1] },
                    { label: "Exception-Keeper", color: BLUES[2] },
                    { label: "Inactive-Keeper", color: BLUES[3] },
                  ]}
                />
              </Card>

              <Card
                title="AL3 Mapping to IQ"
                subtitleRight
                subtitle={
                  <span className="italic text-primary">
                    Total of{" "}
                    <span className="font-bold">
                      {renderTotal(counts, "TotalRows")}
                    </span>
                  </span>
                }
              >
                <div className="h-44 mb-2 mt-2">
                  <AL3MappingBarChart data={al3BarData} loading={loading} />
                </div>
                <DotsLegend
                  items={[
                    { label: "Mapped-AL3", color: BLUES[0] },
                    { label: "Not Mapped-AL3", color: BLUES[1] },
                    { label: "Exception-AL3", color: BLUES[2] },
                    { label: "Inactive-AL3", color: BLUES[3] },
                  ]}
                />
              </Card>
            </div>

            {/* BOTTOM ROW */}
            <div
              className={`grid grid-cols-1 gap-4 ${
                canViewRecentActivity ? "md:grid-cols-4" : "md:grid-cols-3"
              }`}
            >
              <Card
                headerVariant="plain"
                title="Producer Code Status"
                subtitleRight
                subtitle={
                  <span className="italic text-primary">
                    Total of{" "}
                    <span className="font-bold">
                      {renderTotal(counts, "TotalRows")}
                    </span>
                  </span>
                }
              >
                <div className="h-44">
                  <ProdCodePieChart data={prodCodePieData} height={180} />
                </div>
                <DotsLegend
                  items={[
                    { label: "Active", color: PCODE_COLORS[0] },
                    { label: "Inactive", color: PCODE_COLORS[1] },
                  ]}
                />
              </Card>

              <Card
                headerVariant="plain"
                title="Carrier Production Status"
                subtitleRight
                subtitle={
                  <span className="italic text-primary">
                    Total of{" "}
                    <span className="font-bold">
                      {inProductionPieTotal > 0
                        ? inProductionPieTotal.toLocaleString()
                        : "—"}
                    </span>
                  </span>
                }
              >
                <div style={{ height: 180 }} className="h-44">
                  <CarrierStatusPieChart
                    counts={counts}
                    data={inProductionPieData}
                    height={180}
                  />
                </div>

                <DotsLegend
                  items={[
                    { label: "In Production", color: PROD_COLORS[0] },
                    { label: "Not In Production", color: PROD_COLORS[1] },
                  ]}
                />
              </Card>

              <Card
                headerVariant="plain"
                title="Mapped Percentage"
                subtitle={[]}
              >
                <MappingPercentageCard rows={mappedPctRows} />
              </Card>

              {canViewRecentActivity && (
                <RecentDataMappingActivity onViewAll={onOpenNotifications} />
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ProducerCodeDashboard;
